/**
 * Labels and placeholders for the art-direction fields.
 *
 * Separated from the panel so the copy is a data structure rather than JSX,
 * and so the "every stored field is presented" property is unit-testable —
 * adding a field to `ART_DIRECTION_TEXT_FIELDS` without adding copy here would
 * otherwise produce a field the model fills in and nobody can see.
 *
 * The placeholders are deliberately CONCRETE. An empty box under "Motion"
 * invites "dynamic and engaging"; a box that shows what a good answer looks
 * like invites an answer an animator could execute.
 *
 * They are ALSO all prefixed "e.g." — without it, a full, plausible sentence
 * sitting in an empty field reads as something the model wrote, and the only
 * thing distinguishing it from a real answer is placeholder grey. Two words
 * remove the ambiguity without giving up the concreteness.
 */
import { ART_DIRECTION_TEXT_FIELDS, type ArtDirectionTextField } from "@kola/shared";

export interface FieldCopy {
  label: string;
  /** One line under the label, saying what the field is FOR. */
  hint: string;
  placeholder: string;
}

export const FIELD_COPY: Record<ArtDirectionTextField, FieldCopy> = {
  logline: {
    label: "Logline",
    hint: "What this video is, and the one feeling it should leave behind.",
    placeholder: "e.g. A quiet walkthrough of how the thing actually works.",
  },
  mood: {
    label: "Mood",
    hint: "The emotional register — pace, warmth, how loud it gets.",
    placeholder: "e.g. Unhurried and confiding. Never raises its voice.",
  },
  perspective: {
    label: "Perspective",
    hint: "After Effects camera view, asset depth, and focus.",
    placeholder:
      "e.g. Virtual camera at a high three-quarter angle; flat chart in the middle, photo cut-out in front, background farther back.",
  },
  lighting: {
    label: "Lighting",
    hint: "Key direction and quality, and how it falls across the background. Shadows stay barely there.",
    placeholder: "e.g. One soft key from upper left; the background brightens toward it, no cast shadows.",
  },
  texture: {
    label: "Texture",
    hint: "The background image or visual treatment, and the material of the graphics.",
    placeholder: "e.g. Use my uploaded background, or a soft charcoal texture matching the mood; matte graphics, subtle grain.",
  },
  motion: {
    label: "Motion",
    hint: "How separate assets animate and the After Effects camera moves between them.",
    placeholder: "e.g. Slow parallax pushes. Objects settle; nothing snaps.",
  },
  type: {
    label: "Typography",
    hint: "Families, weights, and how type sits in the frame.",
    placeholder: "e.g. One humanist sans, regular and medium. Baseline-aligned, never centred.",
  },
  avoid: {
    label: "Avoid",
    hint: "The clichés this particular script invites. Be specific.",
    placeholder: "e.g. Cutaway diagrams, floating UI panels, steam as swirly vector.",
  },
};

/** Presentation order — the order an animator reads them in, not storage order. */
export const FIELD_ORDER: readonly ArtDirectionTextField[] = ART_DIRECTION_TEXT_FIELDS;
